import { Shimmer, ShimmerElementType, Stack } from "@fluentui/react";
import { useLayoutEffect, useRef, useState } from "react";
import { debounce } from "../../../Utils/js-common";
import { CUSTOMER_VIEW_BREAKPOINTS } from "../CustomerView.constants";
import * as styles from "./ProductTelemetryChart.styles";

const breakpoints = CUSTOMER_VIEW_BREAKPOINTS;
const shimmerColors = { shimmer: "var(--shimmer-color)" };
const shimmerBg = { shimmer: "var(--shimmer-color)", background: "rgb(240, 240, 240)" };

const shimmerLines = {
  Line1: [
    { type: ShimmerElementType.line, height: 20, width: "10%" },
    { type: ShimmerElementType.gap, height: 20, width: "2%" },
    { type: ShimmerElementType.circle, height: 20, width: "15%" },
    { type: ShimmerElementType.gap, height: 20, width: "40%" },
    { type: ShimmerElementType.line, height: 20, width: "10%" },
    { type: ShimmerElementType.gap, height: 20, width: "2%" },
    { type: ShimmerElementType.circle, height: 20, width: "15%" },
    { type: ShimmerElementType.gap, height: 20, width: "40%" }
  ],
  Line2: [
    { type: ShimmerElementType.line, height: 14, width: "25%" },
    { type: ShimmerElementType.gap, height: 14, width: "25%" },
    { type: ShimmerElementType.line, height: 14, width: "25%" },
    { type: ShimmerElementType.gap, height: 14, width: "25%" }
  ],
  Line3: [
    { type: ShimmerElementType.line, height: 14, width: "18%" },
    { type: ShimmerElementType.gap, height: 14, width: "34%" },
    { type: ShimmerElementType.line, height: 14, width: "18%" },
    { type: ShimmerElementType.gap, height: 14, width: "34%" }
  ],
  Line4: [
    { type: ShimmerElementType.line, height: 14, width: "28%" },
    { type: ShimmerElementType.gap, height: 14, width: "24%" },
    { type: ShimmerElementType.line, height: 14, width: "28%" },
    { type: ShimmerElementType.gap, height: 14, width: "24%" }
  ],
  Line5: [
    { type: ShimmerElementType.line, height: 14, width: "35%" },
    { type: ShimmerElementType.gap, height: 14, width: "17%" },
    { type: ShimmerElementType.line, height: 14, width: "35%" },
    { type: ShimmerElementType.gap, height: 14, width: "17%" }
  ]
};

const shimmerCard = {
  title: [
    { type: ShimmerElementType.gap, height: 19, width: "3%" },
    { type: ShimmerElementType.line, height: 19, width: "55%" },
    { type: ShimmerElementType.gap, height: 19, width: "45%" }
  ],
  body: (
    <Stack>
      <div style={{ height: 112, padding: 5, background: "rgb(240, 240, 240)", margin: 2 }}>
        <Shimmer shimmerElements={shimmerLines.Line1} shimmerColors={shimmerBg} />
        <Shimmer
          shimmerElements={[{ type: ShimmerElementType.line, height: 1, width: "100%" }]}
          style={{ marginTop: 5 }}
          shimmerColors={{ shimmer: "rgba(213, 213, 213, 0.5)" }}
        />
        <Stack tokens={{ childrenGap: 6 }} style={{ marginTop: 10 }}>
          <Shimmer shimmerElements={shimmerLines.Line2} shimmerColors={shimmerBg} />
          <Shimmer shimmerElements={shimmerLines.Line3} shimmerColors={shimmerBg} />
          <Shimmer shimmerElements={shimmerLines.Line4} shimmerColors={shimmerBg} />
          <Shimmer shimmerElements={shimmerLines.Line5} shimmerColors={shimmerBg} />
        </Stack>
      </div>
      <Shimmer
        shimmerElements={[
          { type: ShimmerElementType.gap, height: 16, width: "1%" },
          { type: ShimmerElementType.line, height: 245, width: "98%" },
          { type: ShimmerElementType.gap, height: 16, width: "1%" }
        ]}
        shimmerColors={shimmerColors}
        style={{ marginTop: 5 }}
      />
      <Shimmer
        shimmerElements={[{ type: ShimmerElementType.gap, height: 16, width: "100%" }]}
        shimmerColors={shimmerColors}
      />
      <Shimmer
        shimmerElements={[
          { type: ShimmerElementType.gap, height: 20, width: "3%" },
          { type: ShimmerElementType.line, height: 20, width: "15%" },
          { type: ShimmerElementType.gap, height: 20, width: "5%" },
          { type: ShimmerElementType.line, height: 20, width: "15%" },
          { type: ShimmerElementType.gap, height: 20, width: "65%" }
        ]}
        shimmerColors={shimmerColors}
      />
    </Stack>
  )
};

const cardContent = (
  <Stack tokens={{ childrenGap: 10 }}>
    <Shimmer shimmerElements={shimmerCard.title} shimmerColors={shimmerColors} style={{ marginBottom: 5 }} />
    {shimmerCard.body}
  </Stack>
);

export default function ProductTelemetryShimmerLoader() {
  const [cardWidth, setCardWidth] = useState<number | undefined>(370); // default width
  const containerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const calculateWidth = () => {
      let itemsPerRow = 1;

      for (const bp of breakpoints.sort((a, b) => b.width - a.width)) {
        if (window.innerWidth >= bp.width) {
          itemsPerRow = bp.items;
          break;
        }
      }

      if (containerRef.current) {
        const availableWidth = containerRef.current.offsetWidth;
        const newCardWidth = Math.max(Math.floor(availableWidth / itemsPerRow) - 16, 100); // minus gap
        setCardWidth(newCardWidth);
      }
    };

    const debouncedCalculateWidth = debounce(() => calculateWidth(), 100);

    calculateWidth();
    window.addEventListener("resize", debouncedCalculateWidth);
    return () => window.removeEventListener("resize", debouncedCalculateWidth);
  }, []);

  return (
    <>
      <Stack style={{ flex: "1 1 auto", margin: 20, borderRadius: 10 }}>
        <Shimmer
          shimmerElements={[
            { type: ShimmerElementType.line, height: 32, width: "12%" },
            { type: ShimmerElementType.gap, height: 32, width: "1%" },
            { type: ShimmerElementType.line, height: 32, width: "12%" },
            { type: ShimmerElementType.gap, height: 32, width: "1%" },
            { type: ShimmerElementType.line, height: 32, width: "12%" },
            { type: ShimmerElementType.gap, height: 32, width: "1%" },
            { type: ShimmerElementType.line, height: 32, width: "12%" },
            { type: ShimmerElementType.gap, height: 32, width: "39%" },
            { type: ShimmerElementType.line, height: 32, width: "10%" }
          ]}
          shimmerColors={shimmerColors}
        />
      </Stack>
      <div ref={containerRef} style={styles.containerStyle}>
        {Array.from({ length: 6 }).map((_, index) => (
          <div
            key={`loading-${index}`}
            style={{
              flex: `0 1 ${cardWidth}px`,
              maxWidth: `${cardWidth}px`,
              height: "467px",
              border: "2px solid rgb(240, 240, 240)",
              paddingTop: 10,
              boxSizing: "border-box"
            }}
          >
            {cardContent}
          </div>
        ))}
      </div>
    </>
  );
}
